import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000

export const useCompanyStructureStore = defineStore('companyStructure', {
  state: () => ({
    organizationId: null,
    companies: [],
    fetchedAt: null,
    loading: false,
    error: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,
  },

  actions: {
    async fetchIfStale(organizationId) {
      if (this.isCacheValid && this.organizationId === organizationId) return
      await this.fetchData(organizationId)
    },

    async fetchData(organizationId) {
      this.organizationId = organizationId
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/organizations/${organizationId}/structure`, {}, false)
        if (!res.js_error && res.response.success) {
          this.companies = res.response.data.companies || []
          this.fetchedAt = Date.now()
        } else {
          this.error = 'Failed to load organization structure.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    invalidate() {
      this.fetchedAt = null
    },

    async addCompany(payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', `/api/v1/organizations/${this.organizationId}/companies`, { company: payload }, false)
      if (!res.js_error && res.response.success) {
        this.companies.push({ ...res.response.data, departments: res.response.data.departments || [] })
      }
      return res
    },

    async updateCompany(companyId, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/companies/${companyId}`, { company: payload }, false)
      if (!res.js_error && res.response.success) {
        const company = this.companies.find((c) => c.id === companyId)
        if (company) Object.assign(company, res.response.data)
      }
      return res
    },

    async deleteCompany(companyId) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/companies/${companyId}`, {}, false)
      if (!res.js_error && res.response.success) {
        this.companies = this.companies.filter((c) => c.id !== companyId)
      }
      return res
    },

    async addDepartment(companyId, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', `/api/v1/companies/${companyId}/departments`, { department: payload }, false)
      if (!res.js_error && res.response.success) {
        const company = this.companies.find((c) => c.id === companyId)
        if (company) company.departments.push(res.response.data)
      }
      return res
    },

    async updateDepartment(companyId, departmentId, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/departments/${departmentId}`, { department: payload }, false)
      if (!res.js_error && res.response.success) {
        const company = this.companies.find((c) => c.id === companyId)
        const department = company?.departments.find((d) => d.id === departmentId)
        if (department) Object.assign(department, res.response.data)
      }
      return res
    },

    async deleteDepartment(companyId, departmentId) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/departments/${departmentId}`, {}, false)
      if (!res.js_error && res.response.success) {
        const company = this.companies.find((c) => c.id === companyId)
        if (company) company.departments = company.departments.filter((d) => d.id !== departmentId)
      }
      return res
    },
  },
})
